import { useState, useRef } from 'react';
import { X, Circle } from 'lucide-react';
import type { FileType } from '@/routes/chat/hooks/use-chat';
import clsx from 'clsx';

export interface EditorTab {
  id: string;
  file: FileType;
  isModified: boolean;
  isActive: boolean;
}

export interface EditorTabsProps {
  tabs: EditorTab[];
  activeTabId: string;
  onTabClick: (tabId: string) => void;
  onTabClose: (tabId: string) => void;
  onTabReorder: (fromIndex: number, toIndex: number) => void;
  maxTabWidth?: number;
}

export function EditorTabs({
  tabs,
  activeTabId,
  onTabClick,
  onTabClose,
  onTabReorder,
  maxTabWidth = 200
}: EditorTabsProps) {
  const [draggedTab, setDraggedTab] = useState<string | null>(null);
  const [dragOverTab, setDragOverTab] = useState<string | null>(null);
  const tabsContainerRef = useRef<HTMLDivElement>(null);

  // Handle tab drag start
  const handleDragStart = (e: React.DragEvent, tabId: string) => {
    setDraggedTab(tabId);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', tabId);
  };

  // Handle tab drag over
  const handleDragOver = (e: React.DragEvent, tabId: string) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverTab(tabId);
  };

  // Handle tab drag leave
  const handleDragLeave = () => {
    setDragOverTab(null);
  };

  // Handle tab drop
  const handleDrop = (e: React.DragEvent, targetTabId: string) => {
    e.preventDefault();
    
    if (!draggedTab || draggedTab === targetTabId) {
      setDraggedTab(null);
      setDragOverTab(null);
      return;
    }

    const fromIndex = tabs.findIndex(tab => tab.id === draggedTab);
    const toIndex = tabs.findIndex(tab => tab.id === targetTabId);

    if (fromIndex !== -1 && toIndex !== -1) {
      onTabReorder(fromIndex, toIndex);
    }

    setDraggedTab(null);
    setDragOverTab(null);
  };

  // Handle drag end
  const handleDragEnd = () => {
    setDraggedTab(null);
    setDragOverTab(null);
  };

  // Get file icon based on file extension
  const getFileIcon = (filePath: string) => {
    const ext = filePath.split('.').pop()?.toLowerCase();
    const iconMap: Record<string, string> = {
      'js': '🟨',
      'jsx': '⚛️',
      'ts': '🔷',
      'tsx': '⚛️',
      'html': '🌐',
      'css': '🎨',
      'scss': '🎨',
      'json': '📄',
      'md': '📝',
      'py': '🐍',
      'java': '☕',
      'cpp': '⚙️',
      'c': '⚙️',
      'php': '🐘',
      'rb': '💎',
      'go': '🐹',
      'rs': '🦀',
      'vue': '💚',
      'svelte': '🧡',
    };
    return iconMap[ext || ''] || '📄';
  };

  // Get tab width based on number of tabs
  const getTabWidth = () => {
    if (!tabsContainerRef.current) return maxTabWidth;
    
    const containerWidth = tabsContainerRef.current.offsetWidth;
    const availableWidth = containerWidth - 40; // Account for padding
    const calculatedWidth = Math.max(120, Math.min(maxTabWidth, availableWidth / tabs.length));
    
    return calculatedWidth;
  };

  const tabWidth = getTabWidth();

  if (tabs.length === 0) {
    return null;
  }

  return (
    <div 
      ref={tabsContainerRef}
      className="flex items-center bg-gray-50 dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700 overflow-x-auto scrollbar-thin scrollbar-thumb-gray-300 dark:scrollbar-thumb-gray-600"
    >
      <div className="flex items-center min-w-0">
        {tabs.map((tab) => {
          const isActive = tab.id === activeTabId;
          const isDragged = draggedTab === tab.id;
          const isDragOver = dragOverTab === tab.id;
          const fileName = tab.file.filePath.split('/').pop() || 'Untitled';

          return (
            <div
              key={tab.id}
              draggable
              onDragStart={(e) => handleDragStart(e, tab.id)}
              onDragOver={(e) => handleDragOver(e, tab.id)}
              onDragLeave={handleDragLeave}
              onDrop={(e) => handleDrop(e, tab.id)}
              onDragEnd={handleDragEnd}
              className={clsx(
                'flex items-center gap-2 px-3 py-2 border-r border-gray-200 dark:border-gray-700 cursor-pointer transition-all duration-200 group relative',
                {
                  'bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 border-b-2 border-blue-500': isActive,
                  'bg-gray-50 dark:bg-gray-900 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800': !isActive,
                  'opacity-50': isDragged,
                  'bg-blue-50 dark:bg-blue-900/20': isDragOver && !isDragged,
                }
              )}
              style={{ 
                width: `${tabWidth}px`,
                minWidth: '120px',
                maxWidth: `${maxTabWidth}px`
              }}
              onClick={() => onTabClick(tab.id)}
            >
              {/* File icon */}
              <span className="text-sm flex-shrink-0">
                {getFileIcon(tab.file.filePath)}
              </span>

              {/* File name */}
              <span className="text-sm truncate flex-1 min-w-0">
                {fileName}
              </span>

              {/* Modified indicator */}
              {tab.isModified && (
                <Circle className="size-2 fill-current text-orange-500 flex-shrink-0" />
              )}

              {/* Close button */}
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onTabClose(tab.id);
                }}
                className="flex-shrink-0 p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-600 opacity-0 group-hover:opacity-100 transition-opacity"
                title="Close tab"
              >
                <X className="size-3" />
              </button>

              {/* Drag indicator */}
              {isDragOver && !isDragged && (
                <div className="absolute left-0 top-0 bottom-0 w-0.5 bg-blue-500" />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

/**
 * Hook for managing editor tabs state
 */
export function useEditorTabs() {
  const [tabs, setTabs] = useState<EditorTab[]>([]);
  const [activeTabId, setActiveTabId] = useState<string>('');

  // Open a new tab or switch to existing one
  const openTab = (file: FileType) => {
    const existingTab = tabs.find(tab => tab.file.filePath === file.filePath);
    
    if (existingTab) {
      // Switch to existing tab
      setActiveTabId(existingTab.id);
    } else {
      // Create new tab
      const newTab: EditorTab = {
        id: `tab-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        file,
        isModified: false,
        isActive: true
      };
      
      setTabs(prev => [...prev, newTab]);
      setActiveTabId(newTab.id);
    }
  };

  // Close a tab
  const closeTab = (tabId: string) => {
    setTabs(prev => {
      const newTabs = prev.filter(tab => tab.id !== tabId);
      
      // If closing active tab, switch to another tab
      if (tabId === activeTabId && newTabs.length > 0) {
        const closedTabIndex = prev.findIndex(tab => tab.id === tabId);
        const nextTabIndex = Math.min(closedTabIndex, newTabs.length - 1);
        setActiveTabId(newTabs[nextTabIndex].id);
      } else if (newTabs.length === 0) {
        setActiveTabId('');
      }
      
      return newTabs;
    });
  };

  // Switch to a tab
  const switchToTab = (tabId: string) => {
    setActiveTabId(tabId);
  };

  // Reorder tabs
  const reorderTabs = (fromIndex: number, toIndex: number) => {
    setTabs(prev => {
      const newTabs = [...prev];
      const [movedTab] = newTabs.splice(fromIndex, 1);
      newTabs.splice(toIndex, 0, movedTab);
      return newTabs;
    });
  };

  // Mark tab as modified
  const markTabModified = (tabId: string, isModified: boolean) => {
    setTabs(prev => prev.map(tab => 
      tab.id === tabId ? { ...tab, isModified } : tab
    ));
  };

  // Get active tab
  const activeTab = tabs.find(tab => tab.id === activeTabId);

  // Get active file
  const activeFile = activeTab?.file;

  return {
    tabs,
    activeTabId,
    activeTab,
    activeFile,
    openTab,
    closeTab,
    switchToTab,
    reorderTabs,
    markTabModified
  };
}
